import { Component, OnInit, inject } from '@angular/core'
import { MessageService } from 'primeng/api'

import { CambioDeRegla, DueDateMode, InvoiceDueDateRule } from '../data/interfaces/invoice-rule.interface'
import { ApiInvoiceRulesService } from '../data/services/api/api-invoice-rules.service'
import { AuthService } from '@data/services/modules/auth/auth.service'

/** Los modos, con el nombre que tienen para quien los elige. */
const MODOS: { value: DueDateMode; label: string; help: string }[] = [
  { value: 'DAYS_AFTER', label: 'N días después de emitirse', help: 'La factura vence el número de días que elijas contado desde su fecha de emisión.' },
  { value: 'ON_ISSUE', label: 'El mismo día que se emite', help: 'La factura es exigible desde que se emite. El atraso empieza al día siguiente.' },
  { value: 'CONTRACT_DATE', label: 'La fecha pactada en el contrato', help: 'Usa la fecha de pago acordada en la cotización. Hoy solo el residual la tiene.' },
  { value: 'UNDEFINED', label: 'Sin definir — no vence', help: 'La factura no vence, no acumula días de atraso y no genera mora. Se queda a la vista como pendiente de decidir.' },
]

/**
 * Reglas de facturación: cuándo vence cada tipo de factura.
 *
 * ## Por qué esta pantalla existe
 *
 * La renta saca su vencimiento de la letra de la tabla de amortización. Ningún otro rubro tiene
 * letra de la que sacarlo, y la hoja de Contabilidad tampoco lo trae: en la carga de septiembre
 * **144 de 382 renglones llegaron sin fecha de pagaré**, casi todos de gestión vehicular, seguro,
 * pago inicial, residuales y moratorios.
 *
 * De esa fecha cuelgan los días de atraso, la mora y el «vencido al día de corte». Elegirla desde el
 * código sería decidir por Contabilidad, en silencio, sobre una cifra que cobra intereses. Aquí la
 * deciden ellos, queda con su motivo y con quién la puso, y lo que nadie ha decidido se ve que falta.
 */
@Component({
  selector: 'app-invoice-rules-list',
  templateUrl: './invoice-rules-list.component.html',
  styleUrls: ['./invoice-rules-list.component.scss'],
})
export class InvoiceRulesListComponent implements OnInit {
  private readonly api = inject(ApiInvoiceRulesService)
  private readonly auth = inject(AuthService)
  private readonly toast = inject(MessageService)

  reglas: InvoiceDueDateRule[] = []
  cargando = true

  readonly modos = MODOS
  readonly puedeEditar = this.auth.userHasAuthorization('invoices', 'update')

  // ── El diálogo ────────────────────────────────────────────────────────────────────
  editando: InvoiceDueDateRule | null = null
  modo: DueDateMode = 'UNDEFINED'
  dias: number | null = null
  notas = ''
  motivo = ''
  guardando = false
  errorAlGuardar = ''

  ngOnInit(): void {
    this.cargar()
  }

  cargar(): void {
    this.cargando = true
    this.api.findAll().subscribe((r) => {
      this.reglas = r
      this.cargando = false
    })
  }

  // ── Lectura ───────────────────────────────────────────────────────────────────────

  /** La regla en una frase, como la leería quien no escribió el código. */
  enPalabras(r: InvoiceDueDateRule): string {
    switch (r.mode) {
      case 'PROMISSORY':
        return 'El día de su letra del contrato'
      case 'ON_ISSUE':
        return 'El mismo día que se emite'
      case 'CONTRACT_DATE':
        return 'La fecha pactada en el contrato'
      case 'DAYS_AFTER':
        return r.days === 0 ? 'El mismo día que se emite' : `${r.days} día${r.days === 1 ? '' : 's'} después de emitirse`
      default:
        return 'Sin definir'
    }
  }

  /**
   * Las que hay que atender: sin regla y con facturas esperándola.
   *
   * Una clasificación sin definir pero sin ninguna factura no es una tarea, es una casilla vacía. Lo
   * que duele es la que tiene sesenta facturas que no vencen.
   *
   *! «Sin clasificar» queda fuera aunque cumpla las dos condiciones. Ahí no falta una regla: faltan
   *! las clasificaciones de esas facturas, y contarlas aquí mandaría a Contabilidad a definir una
   *! regla que no arreglaría nada. Su aviso es el de abajo, y apunta a CENTRAL.
   */
  get pendientes(): InvoiceDueDateRule[] {
    return this.reglas.filter((r) => r.mode === 'UNDEFINED' && r.withoutDueDate > 0 && !this.esSinClasificar(r))
  }

  /** Las facturas que ni siquiera tienen tipo. No se arreglan con una regla. */
  get sinClasificar(): InvoiceDueDateRule | null {
    return this.reglas.find((r) => this.esSinClasificar(r) && r.withoutDueDate > 0) || null
  }

  get facturasSinVencer(): number {
    return this.pendientes.reduce((t, r) => t + r.withoutDueDate, 0)
  }

  /** La renta no se toca: su fecha sale de la letra del contrato, no de una opinión. */
  esRenta(r: InvoiceDueDateRule): boolean {
    return r.classification === 'RENT'
  }

  /** «Sin clasificar» tampoco: no es un tipo de factura, es el hueco de las que nadie clasificó. */
  esSinClasificar(r: InvoiceDueDateRule): boolean {
    return r.classification === 'UNCLASSIFIED'
  }

  /** Las dos filas que no admiten regla, por razones distintas. */
  esFija(r: InvoiceDueDateRule): boolean {
    return this.esRenta(r) || this.esSinClasificar(r)
  }

  // ── Edición ───────────────────────────────────────────────────────────────────────

  abrir(r: InvoiceDueDateRule): void {
    if (this.esFija(r) || !this.puedeEditar) return
    this.editando = r
    this.modo = r.mode === 'PROMISSORY' ? 'UNDEFINED' : r.mode
    this.dias = r.days
    this.notas = r.notes || ''
    this.motivo = ''
    this.errorAlGuardar = ''
  }

  cerrar(): void {
    this.editando = null
  }

  get ayudaDelModo(): string {
    return MODOS.find((m) => m.value === this.modo)?.help || ''
  }

  get motivoSuficiente(): boolean {
    return this.motivo.trim().length >= 10
  }

  get puedeGuardar(): boolean {
    if (!this.motivoSuficiente || this.guardando) return false
    if (this.modo === 'DAYS_AFTER') return this.dias != null && Number.isInteger(this.dias) && this.dias >= 0 && this.dias <= 365
    return true
  }

  guardar(): void {
    if (!this.editando || !this.puedeGuardar) return
    this.guardando = true
    this.errorAlGuardar = ''

    const cambio: CambioDeRegla = { mode: this.modo, days: this.modo === 'DAYS_AFTER' ? this.dias : null, notes: this.notas, reason: this.motivo.trim() }

    this.api.update(this.editando.classification, cambio).subscribe((r) => {
      this.guardando = false
      if (r.error || !r.data) {
        //* El rechazo se queda EN el diálogo, junto al campo que lo causó. El toast se va a los seis
        //* segundos y deja el formulario abierto sin explicación, y quien lo escribió vuelve a darle
        //* a Guardar con lo mismo.
        this.errorAlGuardar = r.msg
        return
      }
      const n = r.data.recalculated
      this.toast.add({
        severity: 'success',
        summary: 'Regla guardada',
        //* El número no es decoración: es la diferencia entre haber movido cero facturas y haber
        //* movido ciento cuarenta vencimientos que ahora sí cuentan días de atraso.
        detail: n ? `${n} factura${n === 1 ? '' : 's'} cambiaron de fecha de vencimiento` : 'Ninguna factura cambió de fecha',
        life: 6000,
      })
      this.cerrar()
      this.cargar()
    })
  }
}
